import { AdminPage, AdminPageHeader } from '../../components/layout/AdminPage';
import { IconRefresh } from '@tabler/icons-react';
import { SocketSessionRetry } from '../../components/SocketSessionRetry';
import { useState, useEffect } from 'react';
import { useQuery, useQueryClient } from '@tanstack/react-query';
import { useTranslation } from 'react-i18next';
import { api } from '../../lib/api';
import { useBreakpoint } from '../../hooks/useBreakpoint';
import { useTournamentSocket } from '../../hooks/useTournamentSocket';
import { useAuthStore } from '../../store/auth.store';
import TableDetailPanel from './TableDetailPanel';

interface SeatEntry {
  id: string;
  playerName: string;
  status: string;
  currentChips: number;
  memberId: string | null;
}

interface SeatAssignment {
  id: string;
  entryId: string;
  seatNo: number;
  tournamentId: string;
  entry: SeatEntry | null;
}

interface TournamentInfo {
  id: string;
  name: string;
  status: string;
  prizePool: number;
}

export interface TableOverview {
  table: { id: string; tableNo: number; name: string | null; maxSeats: number };
  assignment: { id: string; tournamentId: string; isFinalTable: boolean } | null;
  tournament: TournamentInfo | null;
  seats: SeatAssignment[];
}

type SeatTone = 'playing' | 'checkedIn' | 'eliminated' | 'empty';

function seatTone(status?: string): SeatTone {
  switch (status) {
    case 'PLAYING':
    case 'FINAL_TABLE':
      return 'playing';
    case 'CHECKED_IN':
      return 'checkedIn';
    case 'ELIMINATED':
      return 'eliminated';
    default:
      return 'empty';
  }
}

// Semantic tones only; the legend under each grid names them in words.
const SEAT_TONE_CLASSES: Record<SeatTone, string> = {
  playing: 'bg-success/10 border-success text-base-content',
  checkedIn: 'bg-warning/10 border-warning text-base-content',
  eliminated: 'border-error border-dashed text-base-content/70',
  empty: 'border-base-300 text-base-content/70',
};

const SEAT_TONES: SeatTone[] = ['playing', 'checkedIn', 'eliminated', 'empty'];

export default function TableManagementPage() {
  const { t } = useTranslation('venue');
  const { isMobile } = useBreakpoint();
  const qc = useQueryClient();
  const token = useAuthStore((s) => s.accessToken) ?? '';
  const [selectedTableId, setSelectedTableId] = useState<string | null>(null);
  const [filterTournamentId, setFilterTournamentId] = useState<string>('all');

  const {
    data: tables = [],
    isLoading,
    isFetching,
    isError,
  } = useQuery<TableOverview[]>({
    queryKey: ['tables-overview'],
    queryFn: () => api.get<TableOverview[]>('/venue/tables/overview'),
    refetchInterval: 10_000,
  });

  const { data: tournaments = [] } = useQuery<Array<{ id: string; name: string; status: string }>>({
    queryKey: ['tournaments-active'],
    queryFn: () =>
      api
        .get<{
          data: Array<{ id: string; name: string; status: string }>;
          total: number;
        }>('/tournament?limit=50')
        .then((d) => d.data ?? []),
    staleTime: 30_000,
  });

  // WS sync for the filtered tournament
  const wsTargetId = filterTournamentId !== 'all' ? filterTournamentId : undefined;
  const { sessionPaused, retrySocket, lastElimination, prizePoolUpdate, tournamentStatus } =
    useTournamentSocket(wsTargetId, token, !!wsTargetId);

  // Invalidate on WS events
  useEffect(() => {
    if (lastElimination) qc.invalidateQueries({ queryKey: ['tables-overview'] });
  }, [lastElimination, qc]);

  useEffect(() => {
    if (prizePoolUpdate) qc.invalidateQueries({ queryKey: ['tables-overview'] });
  }, [prizePoolUpdate, qc]);

  useEffect(() => {
    if (tournamentStatus) qc.invalidateQueries({ queryKey: ['tables-overview'] });
  }, [tournamentStatus, qc]);

  // Filter tables by tournament
  const filteredTables =
    filterTournamentId === 'all'
      ? tables
      : tables.filter((t) => t.assignment?.tournamentId === filterTournamentId);

  const selectedTable = filteredTables.find((t) => t.table.id === selectedTableId) ?? null;

  // Unique tournaments that have tables assigned
  const assignedTournaments = tournaments.filter((t) =>
    tables.some((tbl) => tbl.assignment?.tournamentId === t.id),
  );

  return (
    <AdminPage>
      <SocketSessionRetry paused={sessionPaused} retry={retrySocket} />
      <AdminPageHeader
        title={t('tableManagement')}
        actions={
          <div className="flex flex-wrap items-end gap-3">
            <div>
              <label
                htmlFor="table-tournament-filter"
                className="block mb-1 text-sm font-medium text-base-content"
              >
                {t('tournamentFilter')}
              </label>
              <select
                id="table-tournament-filter"
                value={filterTournamentId}
                onChange={(e) => setFilterTournamentId(e.target.value)}
                className="h-11 px-3 bg-base-100 border border-base-300 text-base text-base-content"
              >
                <option value="all">{t('allTables')}</option>
                {assignedTournaments.map((t) => (
                  <option key={t.id} value={t.id}>
                    {t.name}
                  </option>
                ))}
              </select>
            </div>
            <button
              type="button"
              onClick={() => qc.invalidateQueries({ queryKey: ['tables-overview'] })}
              aria-label={t('refreshTables')}
              className="inline-flex h-11 w-11 items-center justify-center border border-base-300 text-base-content hover:bg-base-300"
            >
              <IconRefresh
                size={20}
                stroke={1.5}
                aria-hidden="true"
                className={isFetching ? 'animate-spin' : ''}
              />
            </button>
            <p className="min-h-11 flex items-center text-sm text-base-content/70 tabular-nums">
              {t('tableCountSummary', {
                total: filteredTables.length,
                active: filteredTables.filter((tb) => tb.assignment).length,
              })}
            </p>
          </div>
        }
      />

      {isLoading ? (
        <p role="status" className="text-center py-20 text-base-content/70">
          {t('loading')}
        </p>
      ) : isError ? (
        <div role="alert" className="border border-error p-4 space-y-3">
          <p>{t('seatUx.overviewError')}</p>
          <button
            type="button"
            disabled={isFetching}
            onClick={() => qc.invalidateQueries({ queryKey: ['tables-overview'] })}
            className="min-h-11 border border-base-300 px-3"
          >
            {t('seatUx.refresh')}
          </button>
        </div>
      ) : filteredTables.length === 0 ? (
        <p className="text-center py-20 text-base-content">{t('noPokertables')}</p>
      ) : (
        <>
          <p className="mb-3 flex flex-wrap gap-x-4 gap-y-1 text-sm text-base-content/70">
            {SEAT_TONES.map((tone) => (
              <span key={tone} className="inline-flex items-center gap-1.5">
                <span
                  aria-hidden="true"
                  className={`inline-block h-3 w-3 border ${SEAT_TONE_CLASSES[tone]}`}
                />
                {t(`seatLegend.${tone}`)}
              </span>
            ))}
          </p>
          <ul
            className={`grid gap-4 ${isMobile ? 'grid-cols-1' : 'grid-cols-2 lg:grid-cols-3 xl:grid-cols-4'}`}
          >
            {filteredTables.map((item) => {
              const filledSeats = item.seats.length;
              const maxSeats = item.table.maxSeats;
              const hasAssignment = !!item.assignment;
              const isFull = filledSeats === maxSeats;
              const emptyCount = maxSeats - filledSeats;
              const selected = selectedTableId === item.table.id;

              return (
                <li key={item.table.id}>
                  <button
                    type="button"
                    onClick={() => setSelectedTableId(item.table.id)}
                    aria-haspopup="dialog"
                    aria-label={t('tableCardLabel', {
                      no: item.table.tableNo,
                      tournament: item.tournament?.name ?? t('idle'),
                      filled: filledSeats,
                      max: maxSeats,
                    })}
                    className={`block w-full h-full text-left bg-base-200 border-2 transition-colors hover:bg-base-300 ${
                      selected ? 'border-primary' : 'border-base-300'
                    }`}
                  >
                    {/* Card Header */}
                    <span className="block p-4 border-b border-base-300">
                      <span className="flex items-center justify-between gap-2 mb-1">
                        <span className="font-bold text-base-content">
                          {t('tableLabel', { no: item.table.tableNo })}
                          {item.table.name && (
                            <span className="text-sm font-normal text-base-content/70 ml-2">
                              {item.table.name}
                            </span>
                          )}
                        </span>
                        <span className="flex items-center gap-2">
                          {hasAssignment && emptyCount > 0 && (
                            <span className="text-xs px-1.5 py-0.5 bg-base-300 text-base-content">
                              {t('emptySeats', { count: emptyCount })}
                            </span>
                          )}
                          <span
                            className={`text-xs px-2 py-0.5 font-medium tabular-nums ${
                              isFull
                                ? 'border border-success text-base-content'
                                : !hasAssignment
                                  ? 'bg-base-300 text-base-content'
                                  : 'bg-primary/10 text-primary'
                            }`}
                          >
                            {filledSeats}/{maxSeats}
                          </span>
                        </span>
                      </span>
                      <span
                        className={`block text-sm truncate ${item.tournament ? 'text-primary' : 'text-base-content/70'}`}
                      >
                        {item.tournament ? item.tournament.name : t('idle')}
                      </span>
                    </span>

                    {/* Seat grid preview: number or initial, status as tone + legend */}
                    <span className="block p-3" aria-hidden="true">
                      <span className="grid grid-cols-5 gap-1.5">
                        {Array.from({ length: maxSeats }, (_, i) => {
                          const seat = item.seats.find((s) => s.seatNo === i + 1);
                          const tone = seat ? seatTone(seat.entry?.status) : 'empty';
                          return (
                            <span
                              key={i + 1}
                              className={`h-10 flex items-center justify-center text-xs font-medium border tabular-nums ${SEAT_TONE_CLASSES[tone]}`}
                              title={seat?.entry?.playerName ?? t('emptySeatTitle', { no: i + 1 })}
                            >
                              {seat ? (seat.entry?.playerName?.charAt(0) ?? '?') : i + 1}
                            </span>
                          );
                        })}
                      </span>
                    </span>

                    {/* Player names (compact list) */}
                    {item.seats.length > 0 && (
                      <span className="block px-3 pb-3 text-xs text-base-content/70 space-y-0.5">
                        {item.seats.slice(0, 4).map((s) => (
                          <span key={s.id} className="flex items-center gap-2">
                            <span className="w-5 tabular-nums">{s.seatNo}</span>
                            <span className="truncate text-base-content">
                              {s.entry?.playerName ?? '?'}
                            </span>
                            {s.entry?.status && (
                              <span className="ml-auto shrink-0">
                                {t(`tournament:playerStatus.${s.entry.status}`)}
                              </span>
                            )}
                          </span>
                        ))}
                        {item.seats.length > 4 && (
                          <span className="block">
                            {t('moreSeats', { count: item.seats.length - 4 })}
                          </span>
                        )}
                      </span>
                    )}
                  </button>
                </li>
              );
            })}
          </ul>
        </>
      )}

      {/* Detail Panel */}
      {selectedTable && (
        <>
          {!isMobile && (
            <div className="fixed inset-0 z-40" onClick={() => setSelectedTableId(null)} />
          )}
          <TableDetailPanel
            key={`${selectedTable.table.id}:${selectedTable.assignment?.tournamentId ?? 'idle'}`}
            overviewUnavailable={isError}
            data={selectedTable}
            tournaments={tournaments}
            allTables={filteredTables}
            onClose={() => setSelectedTableId(null)}
          />
        </>
      )}
    </AdminPage>
  );
}
